import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Check } from "lucide-react";
import {
  isValidEmail,
  isValidPhone,
  sanitizePhone,
  validateContact,
} from "@/lib/validation";

const API = "/api";

const newSubmissionId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function ContactForm() {
  const [data, setData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
    gdpr_consent: false,
    website: "",
  });
  const [touched, setTouched] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submissionId = useMemo(newSubmissionId, []);

  const errors = validateContact(data);
  const showError = (field) => touched[field] && errors[field];

  const setField = (field, value) =>
    setData((d) => ({ ...d, [field]: value }));

  const markTouched = (field) =>
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }));

  const submit = async (e) => {
    e.preventDefault();
    if (Object.keys(errors).length) {
      // Označit všechna pole jako touched, ať se zobrazí všechny chyby
      setTouched({
        name: true,
        email: true,
        phone: true,
        message: true,
        gdpr_consent: true,
      });
      toast.error(errors.gdpr_consent || "Zkontrolujte prosím vyplněná pole.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, { ...data, submission_id: submissionId });
      setDone(true);
      toast.success("Zpráva odeslána. Děkujeme!");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Příliš mnoho odeslání z této IP. Zkuste to prosím za chvíli.");
      } else {
        toast.error("Odeslání se nezdařilo. Zkuste to prosím znovu.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="bg-cream border border-line rounded-md p-10">
        <div className="w-14 h-14 rounded-full bg-magenta/10 flex items-center justify-center mb-6">
          <Check className="w-7 h-7 text-magenta" />
        </div>
        <h3 className="font-display text-2xl font-medium">Zpráva dorazila.</h3>
        <p className="text-muted2 mt-2 leading-relaxed">
          Děkujeme za vaši zprávu — ozveme se obvykle do jednoho pracovního dne.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      noValidate
      className="bg-white border border-line rounded-md p-6 md:p-10 space-y-4"
    >
      <input
        type="text"
        name="website"
        tabIndex="-1"
        autoComplete="off"
        className="hidden"
        value={data.website}
        onChange={(e) => setField("website", e.target.value)}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          label="Jméno a příjmení *"
          testId="contact-name"
          autoComplete="name"
          value={data.name}
          onChange={(v) => setField("name", v)}
          onBlur={() => markTouched("name")}
          error={showError("name") ? errors.name : null}
        />
        <Field
          label="Firma"
          testId="contact-company"
          autoComplete="organization"
          value={data.company}
          onChange={(v) => setField("company", v)}
        />
        <Field
          label="E-mail *"
          type="email"
          inputMode="email"
          autoComplete="email"
          testId="contact-email"
          value={data.email}
          onChange={(v) => setField("email", v)}
          onBlur={() => markTouched("email")}
          error={showError("email") ? errors.email : null}
        />
        <Field
          label="Telefon"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+420 777 123 456"
          testId="contact-phone"
          value={data.phone}
          onChange={(v) => setField("phone", sanitizePhone(v))}
          onBlur={() => markTouched("phone")}
          error={showError("phone") ? errors.phone : null}
        />
      </div>

      <div>
        <label className="block text-xs uppercase tracking-wider text-muted2 mb-1.5">Zpráva *</label>
        <textarea
          data-testid="contact-message"
          rows={5}
          value={data.message}
          onChange={(e) => setField("message", e.target.value)}
          onBlur={() => markTouched("message")}
          aria-invalid={Boolean(showError("message"))}
          className={`w-full bg-white border rounded-md px-3 py-2.5 text-sm focus:ring-1 outline-none transition ${
            showError("message")
              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-line focus:border-magenta focus:ring-magenta"
          }`}
          placeholder="O čem si potřebujete pohovořit?"
        />
        {showError("message") && (
          <p className="mt-1.5 text-xs text-red-600">{errors.message}</p>
        )}
      </div>

      <div>
        <label className="flex items-start gap-3 text-sm text-ink/80 cursor-pointer pt-2">
          <input
            type="checkbox"
            data-testid="contact-gdpr"
            checked={data.gdpr_consent}
            onChange={(e) => {
              setField("gdpr_consent", e.target.checked);
              markTouched("gdpr_consent");
            }}
            className="mt-1 accent-magenta w-4 h-4"
          />
          <span>
            Souhlasím se{" "}
            <a href="/zasady-zpracovani-osobnich-udaju" target="_blank" rel="noreferrer" className="text-magenta hover:underline">
              zpracováním osobních údajů
            </a>{" "}
            za účelem vyřízení mé zprávy.
          </span>
        </label>
        {showError("gdpr_consent") && (
          <p className="mt-1.5 text-xs text-red-600">{errors.gdpr_consent}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="contact-submit"
        className="inline-flex items-center justify-center gap-2 bg-magenta hover:bg-magenta-dark disabled:opacity-60 text-white px-7 py-3.5 rounded-full font-medium transition-colors"
      >
        {busy ? "Odesíláme…" : "Odeslat zprávu"} <Send className="w-4 h-4" />
      </button>
    </form>
  );
}

function Field({
  label,
  testId,
  type = "text",
  inputMode,
  autoComplete,
  placeholder,
  value,
  onChange,
  onBlur,
  error,
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-muted2 mb-1.5">{label}</label>
      <input
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={Boolean(error)}
        className={`w-full bg-white border rounded-md px-3 py-2.5 text-sm focus:ring-1 outline-none transition ${
          error
            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
            : "border-line focus:border-magenta focus:ring-magenta"
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// Re-export, kdyby se hodily v testech
export { isValidEmail, isValidPhone };
